
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PendingInvite {
  id: string;
  team_id: string;
  team_name?: string;
  invited_by_email?: string;
  invited_at: string;
}

export function usePendingInvites() {
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPendingInvites = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setPendingInvites([]);
        return;
      }

      const userEmail = session.user.email;
      if (!userEmail) {
        setPendingInvites([]);
        return;
      }

      // Get pending invitations for the user's email
      const { data: invites, error } = await supabase
        .from('team_access')
        .select('id, team_id, invited_at, invited_by')
        .eq('email', userEmail)
        .eq('status', 'invited');

      if (error) {
        console.error("Error fetching pending invites:", error);
        return;
      }

      if (!invites || invites.length === 0) {
        setPendingInvites([]);
        return;
      }

      // For each invitation, get the team name
      const invitesWithTeamInfo = await Promise.all(
        invites.map(async (invite) => {
          // Get team name
          const { data: team } = await supabase
            .from('teamvote_teams')
            .select('name')
            .eq('id', invite.team_id)
            .single();
          
          // Get inviter name from club_central_users if available
          let inviterName = 'Team Coach';
          
          if (invite.invited_by) {
            const { data: user } = await supabase
              .from('club_central_users')
              .select('name')
              .eq('id', invite.invited_by)
              .single();
              
            inviterName = user?.name || 'Team Coach';
          }

          return {
            id: invite.id,
            team_id: invite.team_id,
            team_name: team?.name || 'Unknown Team',
            invited_by_email: inviterName,
            invited_at: invite.invited_at
          };
        })
      );

      setPendingInvites(invitesWithTeamInfo);
    } catch (error) {
      console.error("Error in use-pending-invites:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const acceptInvitation = useCallback(async (inviteId: string) => {
    try {
      const { data: invite } = await supabase
        .from('team_access')
        .select('team_id, email')
        .eq('id', inviteId)
        .single();
        
      if (!invite) {
        toast.error('Invitation not found');
        return false;
      }

      // Call the edge function to activate the invitation
      const { data, error } = await supabase.functions.invoke('activate-team-invitation', {
        body: { teamId: invite.team_id, email: invite.email }
      });

      if (error) {
        console.error('Error activating invitation:', error);
        toast.error('Failed to accept invitation');
        return false;
      }

      if (data?.activated) {
        toast.success('Team invitation accepted');
        fetchPendingInvites(); // Refresh invites
        return true;
      } else {
        toast.warning(data?.message || 'No action taken');
        return false;
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      toast.error('Failed to process invitation');
      return false;
    }
  }, [fetchPendingInvites]);

  const rejectInvitation = useCallback(async (inviteId: string) => {
    try {
      // Call edge function to revoke the invitation
      const { data, error } = await supabase.functions.invoke('revoke-team-access', {
        body: { accessId: inviteId }
      });

      if (error) {
        console.error('Error rejecting invitation:', error);
        toast.error('Failed to reject invitation');
        return false;
      }

      if (data?.success) {
        toast.success('Invitation rejected');
        fetchPendingInvites(); // Refresh invites
        return true;
      } else {
        toast.warning('Failed to reject invitation');
        return false;
      }
    } catch (error) {
      console.error('Error rejecting invitation:', error);
      toast.error('Failed to process rejection');
      return false;
    }
  }, [fetchPendingInvites]);

  useEffect(() => {
    fetchPendingInvites();
    
    // Set up a refresh interval (every 5 minutes)
    const interval = setInterval(fetchPendingInvites, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchPendingInvites]);

  return {
    pendingInvites,
    invitesCount: pendingInvites.length,
    loading,
    refreshInvites: fetchPendingInvites,
    acceptInvitation,
    rejectInvitation
  };
}
